/**
 * useAuthContext — typed hook to consume AuthContext.
 *
 * Lives in shared/ because it is consumed by every feature that needs auth state.
 * AuthProvider (app/providers/AuthProvider.tsx) creates the value; this file
 * owns the context object and the discriminated-union type.
 *
 * Throws if called outside AuthProvider — prevents silent undefined access.
 */
import { createContext, useContext } from 'react';
import type { MeResponse } from '@/shared/types/user';

/**
 * AuthState — discriminated union of all possible auth states exposed by useAuthContext.
 */
export type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: MeResponse; isAdmin: boolean }
  | { status: 'unauthenticated' };

/**
 * AuthContext — provides auth state down the React tree.
 * Value is always non-null inside AuthProvider (useAuthContext throws otherwise).
 */
export const AuthContext = createContext<AuthState | null>(null);

export function useAuthContext(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used inside AuthProvider');
  return ctx;
}
